// @mui material components
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import {
  getFirestore,
  addDoc,
  doc,
  collection,
  serverTimestamp,
  setDoc,
  increment,
} from "firebase/firestore";
import { getStorage, ref, uploadBytes } from "firebase/storage";
import { useState } from "react";

// Material Kit 2 React components
import MKBox from "components/MKBox";
import MKInput from "components/MKInput";
import MKButton from "components/MKButton";
import MKTypography from "components/MKTypography";

import Swal from "sweetalert2";

function Contact() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [secondaryContact, setSecondaryContact] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [currentJob, setCurrentJob] = useState("");
  const [availableToStart, setAvailableToStart] = useState("");
  const [resumeFiles, setResumeFiles] = useState([]);

  const db = getFirestore();
  const storage = getStorage();

  const resetForm = () => {
    setName("");
    setEmail("");
    setSecondaryContact("");
    setSubject("");
    setMessage("");
    setCurrentJob("");
    setAvailableToStart("");
    setResumeFiles([]);
  };

  const handleSubmit = async () => {
    if (
      !name ||
      !email ||
      !secondaryContact ||
      !subject ||
      !message ||
      !currentJob ||
      !availableToStart
    ) {
      Swal.fire({
        title: "Error",
        text: "Please fill all the fields",
        icon: "error",
        confirmButtonText: "OK",
      });
      return;
    }

    if (!email.match(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/)) {
      Swal.fire({
        title: "Error",
        text: "Please enter a valid email",
        icon: "error",
        confirmButtonText: "OK",
      });
      return;
    }

    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const collectionRef = collection(db, "jobApplications");
      const res = await addDoc(collectionRef, {
        name,
        email,
        secondaryContact,
        subject,
        message,
        currentJob,
        availableToStart,
        createdAt: serverTimestamp(),
        timezone,
      });

      // upload resume files if any
      if (resumeFiles.length > 0) {
        for (let i = 0; i < resumeFiles.length; i++) {
          const file = resumeFiles[i];
          const filePath = `jobApplications/${res.id}/${file.name}`;
          const fileRef = ref(storage, filePath);
          await uploadBytes(fileRef, file);
        }
      }

      // update counter in Firestore (optional)
      const docRef = doc(db, "jobApplications", "jobApplicationsData");
      await setDoc(docRef, { count: increment(1) }, { merge: true });

      resetForm();

      Swal.fire({
        title: "Success",
        text: `Your application has been submitted. Request ID: ${res.id}. We will contact you soon.`,
        icon: "success",
        confirmButtonText: "OK",
      });
    } catch (err) {
      Swal.fire({
        title: "Error",
        text: "Something went wrong. Please try again later.",
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };

  return (
    <MKBox component="section" py={{ xs: 2, lg: 6 }}>
      <Container>
        <Grid container item justifyContent="center">
          <MKBox
            width="100%"
            borderRadius="xl"
            shadow="xl"
            mb={6}
            sx={{
              overflow: "hidden",
              background: "white",
              border: "1px solid #eee",
            }}
          >
            <MKBox p={{ xs: 3, sm: 5 }}>
              <MKTypography
                variant="body1"
                color="black"
                mb={3}
                textAlign="center"
                fontWeight="500"
              >
                Fill out the form to apply for a position
              </MKTypography>

              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <MKInput
                    variant="standard"
                    label="Full Name"
                    placeholder="Enter your name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <MKInput
                    variant="standard"
                    label="Email Address"
                    placeholder="example@domain.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <MKInput
                    variant="standard"
                    label="Secondary Contact Info"
                    placeholder="Phone, Messenger, WhatsApp..."
                    value={secondaryContact}
                    onChange={(e) => setSecondaryContact(e.target.value)}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <MKInput
                    variant="standard"
                    label="Department of Interest"
                    placeholder="Development, Marketing, Sales..."
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <MKInput
                    variant="standard"
                    label="Why do you want to join us?"
                    placeholder="Tell us about your motivation..."
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    fullWidth
                    multiline
                    rows={5}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <MKInput
                    variant="standard"
                    label="Current Job Title"
                    placeholder="Senior Software Engineer, Student, etc."
                    value={currentJob}
                    onChange={(e) => setCurrentJob(e.target.value)}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <MKInput
                    type="date"
                    variant="standard"
                    label="Available to Start"
                    value={availableToStart}
                    onChange={(e) => setAvailableToStart(e.target.value)}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <label style={{ marginBottom: "0.5rem", display: "block" }}>
                    Upload your resume and any other relevant files
                  </label>
                  <input
                    type="file"
                    multiple
                    onChange={(e) => setResumeFiles(e.target.files)}
                  />
                </Grid>
              </Grid>

              {/* Submit Button */}
              <Grid container justifyContent="flex-end" mt={4}>
                <MKButton
                  variant="gradient"
                  sx={{
                    color: "rgba(255, 255, 255,1)",
                    background: "#0A8EF2",
                  }}
                  onClick={handleSubmit}
                >
                  Send Application
                </MKButton>
              </Grid>
            </MKBox>
          </MKBox>
        </Grid>
      </Container>
    </MKBox>
  );
}

export default Contact;
